import React, { useState, useEffect, useRef, useCallback } from "react";
import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { Modal, message } from "antd";
import { openServerUrl, preopenWindow } from "../utils/apiError";
import { reportOpenFailure } from "../utils/openLinkFeedback";
import { apiConfig } from "../authConfig";
import {
  resolveReadableBlobUrl,
  buildOfficeEmbedUrl,
  isLikelyPdfFile,
  isLikelyDocxFile,
} from "../utils/blobPreview";
import { stripTrailingDocumentExtension } from "../utils/artefactDisplayFileName";

/** Stripped basename for Modal / iframe aria titles — MIME detection still uses full `fileLabel`. */
function previewAccessibleLabel(fileLabel) {
  const s = String(fileLabel || "").trim();
  if (!s) return "";
  const stripped = stripTrailingDocumentExtension(s);
  return (stripped || s).slice(0, 80);
}

/** Mammoth HTML: basic readability without @tailwindcss/typography */
export const DOCX_PREVIEW_BODY_CLASS =
  "[&_p]:my-2 [&_p]:leading-relaxed [&_h1]:my-3 [&_h1]:text-base [&_h1]:font-bold [&_h2]:mt-3 [&_h2]:text-sm [&_h2]:font-bold [&_h3]:text-sm [&_h3]:font-semibold [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-0.5 [&_table]:my-2 [&_table]:w-full [&_table]:border-collapse [&_table]:text-xs [&_td]:border [&_td]:border-slate-200 [&_td]:p-1.5 [&_th]:border [&_th]:border-slate-200 [&_th]:p-1.5 [&_th]:bg-slate-50";

/**
 * Lazy-loads when scrolled into view (inline).
 * .docx: server-side mammoth HTML (avoids Office Online embed which often cannot fetch Azure SAS).
 * .pdf: iframe to SAS URL. Other Office: Office Web Viewer as last resort.
 *
 * @param {"inline"|"list"} [variant="inline"] — list: icon opens on-demand preview in a modal (same render modes as inline).
 * @param {boolean} [forceLoad=false] — inline only: load preview immediately (e.g. compare modal), skip scroll-into-view.
 * @param {boolean} [fillParent=false] — when true, grow to parent height (e.g. Compare modal) instead of capped 55vh/420px.
 */
export function ArtefactTemplatePreview({ blobUrl, fileLabel, getApiToken, variant = "inline", forceLoad = false, fillParent = false }) {
  const isList = variant === "list";
  const containerRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const loadOnceRef = useRef(false);
  const [status, setStatus] = useState("idle");
  const [previewMode, setPreviewMode] = useState(null); // 'docx-html' | 'pdf' | 'office'
  const [readableUrl, setReadableUrl] = useState(null);
  const [embedSrc, setEmbedSrc] = useState("");
  const [htmlContent, setHtmlContent] = useState("");
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIframeSrc, setViewerIframeSrc] = useState("");
  const [viewerLoading, setViewerLoading] = useState(false);
  const [listModalOpen, setListModalOpen] = useState(false);

  useEffect(() => {
    loadOnceRef.current = false;
    setStatus("idle");
    setPreviewMode(null);
    setReadableUrl(null);
    setEmbedSrc("");
    setHtmlContent("");
    setVisible(false);
    setListModalOpen(false);
  }, [blobUrl, fileLabel]);

  const executeLoad = useCallback(async () => {
    if (!blobUrl) return;
    const label = fileLabel || "";
    setStatus((s) => (s === "idle" ? "loading" : s));
    try {
      if (isLikelyDocxFile(label, blobUrl)) {
        const token = await getApiToken();
        const res = await fetch(
          `${apiConfig.baseUrl}/api/v1/blob-docx-preview-html?url=${encodeURIComponent(blobUrl)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const json = await res.json().catch(() => ({}));
        const html = json?.data?.html;
        if (!res.ok || !json.success || typeof html !== "string") {
          const fallback = await resolveReadableBlobUrl(apiConfig.baseUrl, blobUrl, getApiToken);
          setReadableUrl(fallback || blobUrl);
          setStatus("error");
          return;
        }
        const fresh = await resolveReadableBlobUrl(apiConfig.baseUrl, blobUrl, getApiToken);
        setReadableUrl(fresh || blobUrl);
        setHtmlContent(html);
        setPreviewMode("docx-html");
        setStatus("ready");
        return;
      }

      const safe = await resolveReadableBlobUrl(apiConfig.baseUrl, blobUrl, getApiToken);
      const url = safe || blobUrl;
      setReadableUrl(url);

      if (isLikelyPdfFile(label, blobUrl)) {
        setEmbedSrc(url);
        setPreviewMode("pdf");
        setStatus("ready");
        return;
      }

      setEmbedSrc(buildOfficeEmbedUrl(url));
      setPreviewMode("office");
      setStatus("ready");
    } catch {
      setReadableUrl(blobUrl);
      setStatus("error");
    }
  }, [blobUrl, fileLabel, getApiToken]);

  const titleShort = previewAccessibleLabel(fileLabel);

  useEffect(() => {
    if (isList || !forceLoad || !blobUrl) return undefined;
    setVisible(true);
    if (!loadOnceRef.current) {
      loadOnceRef.current = true;
      void executeLoad();
    }
    return undefined;
  }, [isList, forceLoad, blobUrl, executeLoad]);

  useEffect(() => {
    if (isList || !blobUrl) return undefined;
    const el = containerRef.current;
    if (!el) return undefined;
    if (forceLoad) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisible(true);
          setStatus((s) => (s === "idle" ? "loading" : s));
        }
      },
      { root: null, rootMargin: "120px", threshold: 0.02 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [blobUrl, isList, forceLoad]);

  useEffect(() => {
    if (isList || forceLoad || !visible || !blobUrl || loadOnceRef.current) return;
    loadOnceRef.current = true;
    void executeLoad();
  }, [isList, forceLoad, visible, blobUrl, executeLoad]);

  const openFallbackTab = useCallback(async () => {
    const win = preopenWindow();
    let target = blobUrl;
    try {
      const fresh = await resolveReadableBlobUrl(apiConfig.baseUrl, blobUrl, getApiToken);
      target = fresh || blobUrl;
    } catch { /* fall back to the stored URL */ }
    if (!target) {
      if (win && !win.closed) win.close();
      return;
    }
    if (!openServerUrl(target, win)) reportOpenFailure(message, target);
  }, [blobUrl, getApiToken]);

  /** In-app overlay: PDF uses direct SAS iframe; Office types use Office Web Viewer embed. */
  const openInAppViewer = useCallback(async () => {
    setViewerLoading(true);
    try {
      const fresh = await resolveReadableBlobUrl(apiConfig.baseUrl, blobUrl, getApiToken);
      const u = fresh || blobUrl;
      setReadableUrl(u);
      if (isLikelyPdfFile(fileLabel, blobUrl)) {
        setViewerIframeSrc(u);
      } else {
        setViewerIframeSrc(buildOfficeEmbedUrl(u));
      }
      setViewerOpen(true);
    } finally {
      setViewerLoading(false);
    }
  }, [blobUrl, fileLabel, getApiToken]);

  const handleListOpen = useCallback(() => {
    if (!blobUrl) return;
    setListModalOpen(true);
    if (!loadOnceRef.current) {
      loadOnceRef.current = true;
      void executeLoad();
    }
  }, [blobUrl, executeLoad]);

  const renderReadyInline = () => {
    const docxBox = fillParent
      ? `min-h-0 flex-1 overflow-auto rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-800 shadow-inner ${DOCX_PREVIEW_BODY_CLASS}`
      : `max-h-[min(55vh,420px)] overflow-auto rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-800 shadow-inner ${DOCX_PREVIEW_BODY_CLASS}`;
    const iframeShort = "h-[min(55vh,420px)] w-full rounded-xl border border-[color:var(--color-border)]";
    const iframeFill = "h-full min-h-[200px] w-full flex-1 rounded-xl border border-[color:var(--color-border)]";
    return jsxs(Fragment, {
      children: [
        status === "ready" &&
          previewMode === "docx-html" &&
          htmlContent &&
          jsx("div", {
            className: docxBox,
            children: jsx("div", {
              className: "docx-preview",
              dangerouslySetInnerHTML: { __html: htmlContent },
            }),
          }),
        status === "ready" &&
          previewMode === "pdf" &&
          embedSrc &&
          (fillParent
            ? jsx("div", {
                className: "flex min-h-0 flex-1 flex-col",
                children: jsx("iframe", {
                  title: fileLabel ? `PDF preview: ${titleShort}` : "PDF preview",
                  src: embedSrc,
                  className: `${iframeFill} bg-[color:var(--color-surface-sunken)]`,
                }),
              })
            : jsx("iframe", {
                title: fileLabel ? `PDF preview: ${titleShort}` : "PDF preview",
                src: embedSrc,
                className: `${iframeShort} bg-[color:var(--color-surface-sunken)]`,
              })),
        status === "ready" &&
          previewMode === "office" &&
          embedSrc &&
          (fillParent
            ? jsx("div", {
                className: "flex min-h-0 flex-1 flex-col",
                children: jsx("iframe", {
                  title: fileLabel ? `Template preview: ${titleShort}` : "Template preview",
                  src: embedSrc,
                  className: `${iframeFill} bg-white`,
                  sandbox: "allow-scripts allow-same-origin allow-popups allow-forms",
                }),
              })
            : jsx("iframe", {
                title: fileLabel ? `Template preview: ${titleShort}` : "Template preview",
                src: embedSrc,
                className: `${iframeShort} bg-white`,
                sandbox: "allow-scripts allow-same-origin allow-popups allow-forms",
              })),
      ],
    });
  };

  const renderModalBodyContent = (modalTall) => {
    const box = modalTall ? "min-h-[min(92vh,920px)] max-h-[min(92vh,920px)]" : "";
    const docxScroll = modalTall
      ? "max-h-[min(92vh,920px)] overflow-auto"
      : "max-h-[min(55vh,420px)] overflow-auto";
    const iframeH = modalTall ? "h-[min(92vh,920px)]" : "h-[min(55vh,420px)]";

    if (status === "loading" || (listModalOpen && status === "idle")) {
      return jsx("div", {
        className: `${box} flex items-center justify-center rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] text-sm font-medium text-[color:var(--color-text-muted)]`,
        children: "Loading preview\u2026",
      });
    }
    if (status === "error") {
      return jsxs("div", {
        className:
          "min-h-[200px] rounded-xl border border-[color:var(--color-warn-line)] bg-[color:var(--color-warn-bg)] p-6 flex flex-col items-center justify-center gap-3 text-center",
        children: [
          jsx("p", {
            className: "text-xs text-[color:var(--color-warn-fg)]",
            children: "Preview could not be loaded. Try the in-app viewer or open the file in a new tab.",
          }),
          jsxs("div", {
            className: "flex flex-wrap items-center justify-center gap-3",
            children: [
              jsx("button", {
                type: "button",
                disabled: viewerLoading,
                onClick: () => void openInAppViewer(),
                className:
                  "px-3 py-1.5 rounded-lg bg-[color:var(--color-accent-fill)] text-white text-[10px] font-black uppercase tracking-widest hover:opacity-90 disabled:opacity-50",
                children: viewerLoading ? "Opening\u2026" : "Open in app viewer",
              }),
              jsx("button", {
                type: "button",
                onClick: () => void openFallbackTab(),
                className: "text-[10px] font-bold text-[color:var(--color-accent)] hover:underline",
                children: "Open in new tab",
              }),
            ],
          }),
        ],
      });
    }
    if (status === "ready" && previewMode === "docx-html" && htmlContent) {
      return jsx("div", {
        className: `${docxScroll} rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-800 shadow-inner ${DOCX_PREVIEW_BODY_CLASS}`,
        children: jsx("div", {
          className: "docx-preview",
          dangerouslySetInnerHTML: { __html: htmlContent },
        }),
      });
    }
    if (status === "ready" && previewMode === "pdf" && embedSrc) {
      return jsx("iframe", {
        title: fileLabel ? `PDF preview: ${titleShort}` : "PDF preview",
        src: embedSrc,
        className: `${iframeH} w-full rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)]`,
      });
    }
    if (status === "ready" && previewMode === "office" && embedSrc) {
      return jsx("iframe", {
        title: fileLabel ? `Template preview: ${titleShort}` : "Template preview",
        src: embedSrc,
        className: `${iframeH} w-full rounded-xl border border-slate-200 bg-white`,
        sandbox: "allow-scripts allow-same-origin allow-popups allow-forms",
      });
    }
    return null;
  };

  if (!blobUrl) {
    if (isList) {
      return jsx("span", {
        className: "text-xs text-[color:var(--color-text-muted)]",
        title: "No template file linked",
        children: "-",
      });
    }
    return jsx("div", {
      ref: containerRef,
      className:
        "min-h-[240px] rounded-xl border border-dashed border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] flex items-center justify-center text-xs text-[color:var(--color-text-muted)] px-4 text-center",
      children: "No template file linked",
    });
  }

  if (isList) {
    return jsxs(Fragment, {
      children: [
        jsx("button", {
          type: "button",
          onClick: () => void handleListOpen(),
          title: "Open template preview",
          className:
            "inline-flex items-center justify-center p-2 rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] text-[color:var(--color-text-muted)] hover:bg-[color:var(--color-bg-alt)] hover:text-[color:var(--color-accent)] hover:border-[color:var(--color-info-line)] transition-colors",
          children: jsx("svg", {
            className: "w-5 h-5",
            fill: "none",
            stroke: "currentColor",
            viewBox: "0 0 24 24",
            "aria-hidden": true,
            children: jsx("path", {
              strokeLinecap: "round",
              strokeLinejoin: "round",
              strokeWidth: "2",
              d: "M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14",
            }),
          }),
        }),
        jsx(Modal, {
          title: titleShort ? titleShort : "Template",
          open: listModalOpen,
          onCancel: () => {
            setListModalOpen(false);
          },
          footer: null,
          centered: true,
          width: "min(98vw, 1440px)",
          destroyOnClose: false,
          styles: { body: { padding: 16, minHeight: "min(92vh, 920px)" } },
          children: renderModalBodyContent(true),
        }),
        jsx(Modal, {
          title: titleShort ? titleShort : "Template",
          open: viewerOpen,
          onCancel: () => {
            setViewerOpen(false);
            setViewerIframeSrc("");
          },
          footer: null,
          centered: true,
          width: "min(98vw, 1440px)",
          destroyOnClose: true,
          styles: { body: { padding: 0, height: "min(92vh, 920px)" } },
          children:
            viewerIframeSrc &&
            jsx("iframe", {
              title: "Template viewer",
              src: viewerIframeSrc,
              className: "h-[min(92vh,920px)] w-full border-0 bg-white",
              ...(isLikelyPdfFile(fileLabel, blobUrl)
                ? {}
                : { sandbox: "allow-scripts allow-same-origin allow-popups allow-forms" }),
            }),
        }),
      ],
    });
  }

  const shellClass = fillParent ? "flex h-full min-h-0 w-full flex-col" : "w-full";
  const idleBox =
    fillParent
      ? "flex min-h-[120px] flex-1 items-center justify-center rounded-xl border border-dashed border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] text-xs text-[color:var(--color-text-muted)]"
      : "min-h-[240px] rounded-xl border border-dashed border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] flex items-center justify-center text-xs text-[color:var(--color-text-muted)]";
  const loadingBox =
    fillParent
      ? "flex min-h-[120px] flex-1 items-center justify-center rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] text-xs font-medium text-[color:var(--color-text-muted)]"
      : "min-h-[240px] rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] flex items-center justify-center text-xs font-medium text-[color:var(--color-text-muted)]";
  const errorBox =
    fillParent
      ? "flex min-h-[120px] flex-1 flex-col items-center justify-center gap-2 rounded-xl border border-[color:var(--color-warn-line)] bg-[color:var(--color-warn-bg)] p-4 text-center"
      : "min-h-[200px] rounded-xl border border-[color:var(--color-warn-line)] bg-[color:var(--color-warn-bg)] p-4 flex flex-col items-center justify-center gap-2 text-center";

  return jsxs(Fragment, {
    children: [
      jsxs("div", {
        ref: containerRef,
        className: shellClass,
        children: [
          !visible &&
            !forceLoad &&
            status === "idle" &&
            jsx("div", {
              className: idleBox,
              children: "Scroll to load preview\u2026",
            }),
          status === "loading" &&
            jsx("div", {
              className: loadingBox,
              children: "Loading preview\u2026",
            }),
          status === "error" &&
            jsxs("div", {
              className: errorBox,
              children: [
                jsx("p", {
                  className: "text-xs text-[color:var(--color-warn-fg)]",
                  children: "Preview could not be loaded. Try the in-app viewer or open the file in a new tab.",
                }),
                jsxs("div", {
                  className: "flex flex-wrap items-center justify-center gap-3",
                  children: [
                    jsx("button", {
                      type: "button",
                      disabled: viewerLoading,
                      onClick: () => void openInAppViewer(),
                      className:
                        "px-3 py-1.5 rounded-lg bg-[color:var(--color-accent-fill)] text-white text-[10px] font-black uppercase tracking-widest hover:opacity-90 disabled:opacity-50",
                      children: viewerLoading ? "Opening\u2026" : "Open in app viewer",
                    }),
                    jsx("button", {
                      type: "button",
                      onClick: () => void openFallbackTab(),
                      className: "text-[10px] font-bold text-[color:var(--color-accent)] hover:underline",
                      children: "Open in new tab",
                    }),
                  ],
                }),
              ],
            }),
          renderReadyInline(),
        ],
      }),
      jsx(Modal, {
        title: titleShort ? titleShort : "Template",
        open: viewerOpen,
        onCancel: () => {
          setViewerOpen(false);
          setViewerIframeSrc("");
        },
        footer: null,
        width: "min(96vw, 1200px)",
        style: { top: 24 },
        destroyOnClose: true,
        styles: { body: { padding: 0, height: "min(85vh, 800px)" } },
        children:
          viewerIframeSrc &&
          jsx("iframe", {
            title: "Template viewer",
            src: viewerIframeSrc,
            className: "h-[min(85vh,800px)] w-full border-0 bg-white",
            ...(isLikelyPdfFile(fileLabel, blobUrl)
              ? {}
              : { sandbox: "allow-scripts allow-same-origin allow-popups allow-forms" }),
          }),
      }),
    ],
  });
}
